import React, { useState, useEffect } from 'react';
import './App.css';
import Login from './pages/Login';
import { VerificationCode } from './VerificationCode';
import ContratistasManager from './components/ContratistasManager';
import DocumentTypesManager from './components/DocumentTypesManager';
import DocumentosManager from './components/DocumentosManager';
import VehiculosManager from './components/VehiculosManager';
import PersonalManager from './components/PersonalManager';
import LicenciasManager from './components/LicenciasManager';
import DashboardContratista from './components/DashboardContratista';
import DashboardEmpresa from './components/DashboardEmpresa';
import CargosManager from './components/CargosManager';
import GestionEntregasSeguridad from './components/GestionEntregasSeguridad';
import ArticulosSeguridad from './components/ArticulosSeguridad';
import CategoriasSeguridad from './components/CategoriasSeguridad';
import PautasManager from './components/PautasManager';
import { ChangePassword } from './ChangePassword';
import { API_BASE_URL } from './config';
import packageInfo from '../package.json';
import ThemeToggle from './components/ThemeToggle';
import AutoRegistroFoto from './pages/AutoRegistroFoto';
import Demos from './pages/Demos';
import { usePermission } from './hooks/usePermission';
import RolesManager from './components/RolesManager';
import ComplianceLey21719 from './components/ComplianceLey21719';
import ChatManager from './components/ChatManager';
import AuditoriaManager from './components/AuditoriaManager';

function App() {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    currentStep: 'login', // login, verify-code, change-password, authenticated
    usuario: null,
    token: null,
    usuarioId: null,
    tempEmail: null,
    activeSection: 'dashboard',
    isMobileMenuOpen: false,
    documentosFilter: null,
    chatPersonalDestino: null
  });

  const { can, canView } = usePermission(authState.usuario);

  const isIntegracionesOpen = authState.activeSection === 'integraciones' || authState.activeSection === 'integraciones-previred';

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('usuario');
    if (token && savedUser) {
      const parsed = JSON.parse(savedUser);
      // Si el usuario guardado no tiene permisos, re-verificar con el backend
      if (!parsed.permisos) {
        fetch(`${API_BASE_URL}/api/auth/verify`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(r => r.json())
          .then(data => {
            if (data.success && data.usuario) {
              localStorage.setItem('usuario', JSON.stringify(data.usuario));
              setAuthState(prev => ({
                ...prev,
                isAuthenticated: true,
                currentStep: 'authenticated',
                token: token,
                usuario: data.usuario,
                usuarioId: data.usuario.id
              }));
            } else {
              handleLogout();
            }
          })
          .catch(() => handleLogout());
        return;
      }
      setAuthState(prev => ({
        ...prev,
        isAuthenticated: true,
        currentStep: 'authenticated',
        token: token,
        usuario: parsed,
        usuarioId: parsed.id,
        activeSection: window.location.hash === '#mensajes' ? 'mensajes' : prev.activeSection
      }));
    }
    // Deep-link desde notificación push (#mensajes)
    if (window.location.hash === '#mensajes') {
      const limpiarHash = () => window.history.replaceState(null, '', window.location.pathname);
      setTimeout(() => {
        setAuthState(prev => ({ ...prev, activeSection: 'mensajes' }));
        limpiarHash();
      }, 300);
    }
  }, []);

  const handleLoginSuccess = (data) => {
    if (data.step === 'verify-code') {
      setAuthState(prev => ({
        ...prev,
        currentStep: 'verify-code',
        usuarioId: data.usuarioId,
        tempEmail: data.correo,
        usuario: data.usuario
      }));
    } else if (data.step === 'change-password') {
      setAuthState(prev => ({
        ...prev,
        currentStep: 'change-password',
        usuarioId: data.usuarioId,
        usuario: data.usuario
      }));
    } else if (data.step === 'authenticated') {
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('usuario', JSON.stringify(data.usuario));
      setAuthState({
        isAuthenticated: true,
        currentStep: 'authenticated',
        token: data.token,
        usuario: data.usuario,
        usuarioId: data.usuario.id,
        activeSection: 'dashboard'
      });
    }
  };

  const handleVerificationSuccess = (data) => {
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('usuario', JSON.stringify(data.usuario));
    setAuthState({
      isAuthenticated: true,
      currentStep: 'authenticated',
      token: data.token,
      usuario: data.usuario,
      usuarioId: data.usuario.id,
      activeSection: 'dashboard'
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('usuario');
    setAuthState({
      isAuthenticated: false,
      currentStep: 'login',
      usuario: null,
      token: null,
      usuarioId: null,
      tempEmail: null,
      activeSection: 'dashboard'
    });
  };

  // Verificar si es la ruta de auto-registro
  if (window.location.pathname.startsWith('/auto-registro-foto/')) {
    return <AutoRegistroFoto />;
  }

  // Página pública de demostraciones con videos empotrados
  if (window.location.pathname === '/demos' || window.location.pathname === '/demos/') {
    return <Demos />;
  }

  if (authState.currentStep === 'login') return <Login onLoginSuccess={handleLoginSuccess} />;

  if (authState.currentStep === 'verify-code') {
    return (
      <VerificationCode
        usuarioId={authState.usuarioId}
        correo={authState.tempEmail}
        onVerificationSuccess={handleVerificationSuccess}
        onCancel={() => setAuthState(prev => ({ ...prev, currentStep: 'login' }))}
      />
    );
  }

  if (authState.currentStep === 'change-password') {
    return (
      <ChangePassword
        usuarioId={authState.usuarioId}
        onPasswordChangeSuccess={() => setAuthState(prev => ({ ...prev, currentStep: 'login' }))}
        onCancel={() => setAuthState(prev => ({ ...prev, currentStep: 'login' }))}
      />
    );
  }

  if (authState.isAuthenticated) {
    return (
      <div className={`app-layout ${authState.isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
        <button
          className="mobile-menu-toggle"
          onClick={() => setAuthState(prev => ({ ...prev, isMobileMenuOpen: !prev.isMobileMenuOpen }))}
        >
          {authState.isMobileMenuOpen ? '✕' : '☰'}
        </button>
        <aside className={`sidebar ${authState.isMobileMenuOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h3>Gestión Documental</h3>
            <span>{authState.usuario?.nombre_empresa}</span>
          </div>
            <nav className="nav-menu">
              <button
                className={authState.activeSection === 'dashboard' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'dashboard', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">📊</span> Panel Principal
              </button>

              <button
                className={authState.activeSection === 'contratistas' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'contratistas', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">🏗️</span> Contratistas
              </button>

              <button
                className={authState.activeSection === 'contratista-personal' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'contratista-personal', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">👷</span> Nómina Personal
              </button>

              <button
                className={authState.activeSection === 'licencias-medicas' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'licencias-medicas', licenciasFilter: null, isMobileMenuOpen: false })}
              >
                <span className="nav-icon">🏥</span> Licencias Médicas
              </button>

              <button
                className={authState.activeSection === 'contratista-vehiculos' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'contratista-vehiculos', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">🚚</span> Vehículos
              </button>

              <button
                className={authState.activeSection === 'entregas-seguridad' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'entregas-seguridad', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">📦</span> Entrega de Artículos
              </button>

              <button
                className={authState.activeSection === 'categorias-seguridad' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'categorias-seguridad', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">🏷️</span> Categorías de Artículos
              </button>

              <button
                className={authState.activeSection === 'articulos-seguridad' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'articulos-seguridad', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">🦺</span> Mantenedor de Artículos
              </button>

              {authState.usuario?.tipo === 'M' && (
              <button
                className={authState.activeSection === 'tipos' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'tipos', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">📋</span> Tipos de Documento
              </button>
              )}

              <button
                className={authState.activeSection === 'pautas' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'pautas', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">🛠️</span> Pautas Mantención
              </button>

              <button
                className={authState.activeSection === 'cargos' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'cargos', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">👔</span> Cargos
              </button>

              <button
                className={authState.activeSection === 'documentos' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'documentos', documentosFilter: null, isMobileMenuOpen: false })}
              >
                <span className="nav-icon">📁</span> Expedientes
              </button>

              <button
                className={authState.activeSection === 'mensajes' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'mensajes', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">💬</span> Mensajes
              </button>

              <button
                className={authState.activeSection === 'roles-permisos' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'roles-permisos', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">🔐</span> Roles y Permisos
              </button>

              <button
                className={authState.activeSection === 'compliance-ley' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'compliance-ley', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">🛡️</span> Cumplimiento Ley 21.719
              </button>

              {(authState.usuario?.tipo === 'M' || authState.usuario?.tipo === 'C') && (
              <button
                className={authState.activeSection === 'auditoria' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'auditoria', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">📋</span> Auditoría
              </button>
              )}

            <div className="nav-group">
              <button
                className={authState.activeSection === 'informes' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'informes', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">📊</span> Informes
              </button>
              <button
                className={authState.activeSection === 'integraciones' ? 'active' : ''}
                onClick={() => setAuthState({ ...authState, activeSection: 'integraciones', isMobileMenuOpen: false })}
              >
                <span className="nav-icon">🔗</span> Integraciones
              </button>
              {isIntegracionesOpen && (
                <button
                  className={authState.activeSection === 'integraciones-previred' ? 'active' : ''}
                  onClick={() => setAuthState({ ...authState, activeSection: 'integraciones-previred', isMobileMenuOpen: false })}
                >
                  <span className="nav-icon">📄</span> Previred
                </button>
              )}
            </div>
          </nav>
          <div className="sidebar-footer">
            <div className="app-version">v{packageInfo.version}</div>
            <button onClick={handleLogout}>Cerrar Sesión</button>
          </div>
        </aside>

        <main className="content-area">
          <header className="main-header">
            <h2>
              {authState.activeSection === 'dashboard'
                ? 'Bienvenido'
                : (authState.activeSection === 'contratista-personal' && authState.usuario?.nombre_empresa)
                  ? authState.usuario.nombre_empresa
                : authState.activeSection === 'integraciones-previred'
                  ? 'Previred'
                : authState.activeSection === 'tipos'
                  ? 'Tipos de documento'
                : authState.activeSection === 'entregas-seguridad'
                  ? 'Entrega de Artículos'
                  : authState.activeSection === 'compliance-ley'
                    ? 'Cumplimiento ley 21.719'
                    : authState.activeSection.replace('-', ' ').toUpperCase()
              }
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <ThemeToggle />
              <div className="user-profile">
                <span style={{ fontSize: '1.2rem' }}>👤</span>
                <strong>{authState.usuario?.nombre}</strong>
                <span style={{ color: '#94a3b8', marginLeft: '4px' }}>• {authState.usuario?.rol}</span>
              </div>
            </div>
          </header>

          <div className="component-render">
            {authState.activeSection === 'dashboard' && (
              <div className="dashboard-home">
                <h3>Bienvenido al gestor de documentos</h3>
                <p>Selecciona una opción del menú para comenzar.</p>

                {/* Dashboard para Contratista */}
                {authState.usuario?.tipo === 'C' && authState.usuario?.id_contratista && (
                  <DashboardContratista
                    usuario={authState.usuario}
                    onDrillDownLicencias={(filter) => setAuthState({ ...authState, activeSection: 'licencias-medicas', licenciasFilter: filter })}
                  />
                )}

                {/* Dashboard para Empresa (cualquier tipo que no sea 'C') */}
                {authState.usuario?.tipo !== 'C' && authState.usuario?.empresa_id && (
                  <DashboardEmpresa
                    usuario={authState.usuario}
                    onDrillDown={(filter) => setAuthState({ ...authState, activeSection: 'documentos', documentosFilter: filter })}
                    onDrillDownLicencias={(filter) => setAuthState({ ...authState, activeSection: 'licencias-medicas', licenciasFilter: filter })}
                  />
                )}
              </div>
            )}
            {authState.activeSection === 'contratistas' && <ContratistasManager usuario={authState.usuario} />}
            {authState.activeSection === 'contratista-personal' && (
              <PersonalManager
                usuario={authState.usuario}
                onMensajePersonal={(idPersonal) => setAuthState({ ...authState, activeSection: 'mensajes', chatPersonalDestino: idPersonal, isMobileMenuOpen: false })}
              />
            )}
            {authState.activeSection === 'licencias-medicas' && (
              <LicenciasManager usuario={authState.usuario} initialFilter={authState.licenciasFilter} />
            )}
            {authState.activeSection === 'contratista-vehiculos' && (
              <VehiculosManager usuario={authState.usuario} />
            )}
            {authState.activeSection === 'entregas-seguridad' && (
              <GestionEntregasSeguridad usuario={authState.usuario} />
            )}
            {authState.activeSection === 'tipos' && authState.usuario?.tipo === 'M' && <DocumentTypesManager usuario={authState.usuario} />}
            {authState.activeSection === 'pautas' && <PautasManager usuario={authState.usuario} />}
            {authState.activeSection === 'cargos' && <CargosManager usuario={authState.usuario} />}
            {authState.activeSection === 'articulos-seguridad' && <ArticulosSeguridad usuario={authState.usuario} />}
            {authState.activeSection === 'categorias-seguridad' && <CategoriasSeguridad usuario={authState.usuario} />}
            {authState.activeSection === 'documentos' && <DocumentosManager usuario={authState.usuario} initialFilter={authState.documentosFilter} />}
            {authState.activeSection === 'mensajes' && (
              <ChatManager
                usuario={authState.usuario}
                personalDestinoId={authState.chatPersonalDestino}
                onClearDestino={() => setAuthState(prev => ({ ...prev, chatPersonalDestino: null }))}
              />
            )}
            {authState.activeSection === 'informes' && (
              <div className="placeholder-section">
                <h3>Informes</h3>
                <p>Sección de informes en desarrollo</p>
              </div>
            )}
            {authState.activeSection === 'integraciones' && (
              <div className="placeholder-section">
                <h3>Integraciones</h3>
                <p>Sección de integraciones en desarrollo</p>
              </div>
            )}
            {authState.activeSection === 'integraciones-previred' && (
              <div className="placeholder-section">
                <h3>Previred</h3>
                <p>Sección de Previred en desarrollo</p>
              </div>
            )}
            {authState.activeSection === 'roles-permisos' && (
              <RolesManager usuario={authState.usuario} />
            )}
            {authState.activeSection === 'compliance-ley' && (
              <ComplianceLey21719 usuario={authState.usuario} />
            )}
            {authState.activeSection === 'auditoria' && (
              <AuditoriaManager usuario={authState.usuario} />
            )}
          </div>
        </main>
      </div>
    );
  }

  return <div>Cargando...</div>;
}

export default App;
